import type { User } from './auth'

// Get API URL - use environment variable, or detect production, or fallback to localhost
export function getApiUrl(): string {
  if (process.env.NEXT_PUBLIC_STRAPI_URL) {
    return process.env.NEXT_PUBLIC_STRAPI_URL;
  }
  
  // Detect if we're in production (check both server and client side)
  const productionHosts =
    typeof window !== 'undefined' &&
    ['proslava.ba', 'www.proslava.ba', 'proslava.ba', 'www.proslava.ba'].includes(
      window.location.hostname
    )
  const isProduction =
    process.env.NODE_ENV === 'production' || productionHosts
  
  if (isProduction) {
    return 'https://admin.proslava.ba';
  }
  
  // Fallback to localhost for development
  return 'http://localhost:1337';
}

const API_URL = getApiUrl();

// Helper function to translate English error messages to Serbian
function translateError(errorMessage: string): string {
  if (!errorMessage) return 'Greška'
  
  const translations: { [key: string]: string } = {
    // Auth errors
    'Invalid identifier or password': 'Neispravna email adresa ili lozinka.',
    'Invalid credentials': 'Neispravna email adresa ili lozinka.',
    'Invalid email or password': 'Neispravna email adresa ili lozinka.',
    'Email or Username are already taken': 'Email adresa je već registrovana. Molimo koristite drugu email adresu ili se prijavite.',
    'Email is already taken': 'Email adresa je već registrovana. Molimo koristite drugu email adresu ili se prijavite.',
    'Username is already taken': 'Korisničko ime je već zauzeto. Molimo koristite drugo korisničko ime.',
    'Email or Username already taken': 'Email adresa je već registrovana. Molimo koristite drugu email adresu ili se prijavite.',
    'already taken': 'Email adresa je već registrovana.',
    'User not found': 'Korisnik nije pronađen.',
    'User not confirmed': 'Email adresa nije potvrđena. Molimo proverite vašu email poštu.',
    'Your account email is not confirmed': 'Email adresa nije potvrđena. Molimo proverite vašu email poštu.',
    'Account email is not confirmed': 'Email adresa nije potvrđena. Molimo proverite vašu email poštu.',
    'Email is not confirmed': 'Email adresa nije potvrđena. Molimo proverite vašu email poštu.',
    'email is not confirmed': 'Email adresa nije potvrđena. Molimo proverite vašu email poštu.',
    'account email is not confirmed': 'Email adresa nije potvrđena. Molimo proverite vašu email poštu.',
    'not confirmed': 'Email adresa nije potvrđena. Molimo proverite vašu email poštu.',
    'User is blocked': 'Vaš nalog je blokiran. Molimo kontaktirajte administratora.',
    'Password is incorrect': 'Lozinka nije tačna.',
    'Current password is incorrect': 'Trenutna lozinka nije tačna.',
    'Password must be at least 6 characters': 'Lozinka mora imati najmanje 6 karaktera.',
    'Passwords do not match': 'Lozinke se ne poklapaju.',
    'Password confirmation does not match': 'Potvrda lozinke se ne poklapa.',
    'Invalid token': 'Neispravan token. Molimo pokušajte ponovo.',
    'Token expired': 'Token je istekao. Molimo pokušajte ponovo.',
    'Forbidden': 'Nemate dozvole za ovu akciju.',
    'Unauthorized': 'Niste prijavljeni.',
    'Not found': 'Nije pronađeno.',
    'Internal Server Error': 'Greška na serveru. Molimo pokušajte ponovo kasnije.',
    'Bad Request': 'Neispravan zahtev.',
    'Network error': 'Greška u mreži. Molimo proverite vašu internet konekciju.',
    'Failed to fetch': 'Greška u mreži. Molimo proverite vašu internet konekciju.',
  }
  
  // Check for exact match first
  if (translations[errorMessage]) {
    return translations[errorMessage]
  }
  
  // Check for partial matches (case insensitive)
  const lowerMessage = errorMessage.toLowerCase()
  
  // Check for specific phrases first (longer matches first)
  const phraseMatches = [
    { phrase: 'account email is not confirmed', translation: 'Email adresa nije potvrđena. Molimo proverite vašu email poštu.' },
    { phrase: 'email is not confirmed', translation: 'Email adresa nije potvrđena. Molimo proverite vašu email poštu.' },
    { phrase: 'not confirmed', translation: 'Email adresa nije potvrđena. Molimo proverite vašu email poštu.' },
    { phrase: 'already taken', translation: 'Email adresa je već registrovana. Molimo koristite drugu email adresu ili se prijavite.' },
    { phrase: 'invalid credentials', translation: 'Neispravna email adresa ili lozinka.' },
    { phrase: 'invalid password', translation: 'Neispravna email adresa ili lozinka.' },
    { phrase: 'user not found', translation: 'Korisnik nije pronađen.' },
    { phrase: 'user is blocked', translation: 'Vaš nalog je blokiran. Molimo kontaktirajte administratora.' },
    { phrase: 'token expired', translation: 'Token je istekao. Molimo pokušajte ponovo.' },
    { phrase: 'invalid token', translation: 'Neispravan token. Molimo pokušajte ponovo.' },
  ]
  
  for (const { phrase, translation } of phraseMatches) {
    if (lowerMessage.includes(phrase)) {
      return translation
    }
  }
  
  // Check dictionary translations
  for (const [english, serbian] of Object.entries(translations)) {
    if (lowerMessage.includes(english.toLowerCase())) {
      return serbian
    }
  }
  
  // If no translation found, return original message
  return errorMessage
}

export interface LoginResponse {
  jwt: string
  user: {
    id: number
    username: string
    email: string
    ime?: string
    prezime?: string
    confirmed: boolean
    blocked: boolean
  }
}

export interface RegisterResponse {
  jwt?: string  // Optional - not present if user needs email confirmation
  user: {
    id: number
    username: string
    email: string
    ime?: string
    prezime?: string
    confirmed: boolean
    blocked: boolean
  }
}

export interface AuthError {
  error: {
    status: number
    message: string
  }
}

export async function login(email: string, password: string): Promise<LoginResponse> {
  const response = await fetch(`${API_URL}/api/auth/local`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      identifier: email, // Strapi uses 'identifier' for login (can be email or username)
      password,
    }),
  })

  if (!response.ok) {
    const error: AuthError = await response.json()
    const errorMessage = error.error?.message || 'Greška pri prijavljivanju'
    throw new Error(translateError(errorMessage))
  }

  const loginResponse = await response.json()
  
  // Fetch full user data including ime and prezime
  if (loginResponse.jwt && loginResponse.user) {
    try {
      const userData = await getMe(loginResponse.jwt)
      if (userData) {
        const fullUser = userData.data || userData
        return {
          ...loginResponse,
          user: {
            ...loginResponse.user,
            // Keep all normalized /users/me fields (incl. favorite_services) so
            // client state can hydrate favorites immediately after login.
            ...(fullUser as Record<string, unknown>),
            ime: (fullUser as Record<string, unknown>).ime as string | undefined,
            prezime: (fullUser as Record<string, unknown>).prezime as string | undefined,
          },
        }
      }
    } catch (error) {
      console.warn('Failed to fetch full user data after login:', error)
      // Return original response if getMe fails
    }
  }

  return loginResponse
}

export async function register(
  ime: string,
  prezime: string,
  email: string,
  password: string
): Promise<RegisterResponse> {
  // Generate username from ime and prezime (lowercase, remove spaces)
  const baseUsername = `${ime.trim().toLowerCase()}_${prezime.trim().toLowerCase()}`.replace(/\s+/g, '_')
  
  // Generate a unique sequence: timestamp + random number to ensure uniqueness
  const uniqueId = `${Date.now()}_${Math.floor(Math.random() * 10000)}`
  let finalUsername = `${baseUsername}_${uniqueId}`
  let usernameExists = await checkUsernameExists(finalUsername)
  
  // If username still exists (very unlikely), keep adding random numbers until we find a unique one
  let attempts = 0
  while (usernameExists && attempts < 100) {
    const additionalRandom = Math.floor(Math.random() * 100000)
    finalUsername = `${baseUsername}_${uniqueId}_${additionalRandom}`
    usernameExists = await checkUsernameExists(finalUsername)
    attempts++
  }
  
  // If we still couldn't find a unique username after 100 attempts, use email prefix as fallback
  if (usernameExists) {
    const emailPrefix = email.split('@')[0].toLowerCase().replace(/[^a-z0-9]/g, '_')
    finalUsername = `${emailPrefix}_${Date.now()}_${Math.floor(Math.random() * 100000)}`
  }
  
  // First, register the user without ime and prezime (Strapi doesn't accept custom fields in register)
  const response = await fetch(`${API_URL}/api/auth/local/register`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      username: finalUsername,
      email,
      password,
    }),
  })

  if (!response.ok) {
    const error: AuthError = await response.json()
    let errorMessage = error.error?.message || 'Greška pri registraciji'
    
    // Translate error message
    errorMessage = translateError(errorMessage)
    
    // If it's an email confirmation error, try to proceed anyway
    if (errorMessage.toLowerCase().includes('confirmation email') || 
        (errorMessage.toLowerCase().includes('email') && errorMessage.toLowerCase().includes('potvrda'))) {
      console.warn('Email confirmation failed, but user might still be created')
      throw new Error('Korisnik je kreiran, ali potvrda emaila nije uspela. Molimo kontaktirajte administratora.')
    }
    
    throw new Error(errorMessage)
  }

  const registerResponse = await response.json()
  
  // If user was created and we have a JWT, update the user with ime and prezime
  if (registerResponse.user && registerResponse.jwt) {
    try {
      // Try different formats for updating user
      let updateSuccess = false
      
      // Format 1: Direct fields (Strapi v4 style)
      let updateResponse = await fetch(`${API_URL}/api/users/${registerResponse.user.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${registerResponse.jwt}`,
        },
        body: JSON.stringify({
          ime: ime.trim(),
          prezime: prezime.trim(),
        }),
      })
      
      if (updateResponse.ok) {
        updateSuccess = true
      } else {
        // Format 2: With data wrapper (Strapi v5 style)
        updateResponse = await fetch(`${API_URL}/api/users/${registerResponse.user.id}`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${registerResponse.jwt}`,
          },
          body: JSON.stringify({
            data: {
              ime: ime.trim(),
              prezime: prezime.trim(),
            },
          }),
        })
        
        if (updateResponse.ok) {
          updateSuccess = true
        } else {
          // Format 3: Try PATCH instead of PUT
          updateResponse = await fetch(`${API_URL}/api/users/${registerResponse.user.id}`, {
            method: 'PATCH',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${registerResponse.jwt}`,
            },
            body: JSON.stringify({
              ime: ime.trim(),
              prezime: prezime.trim(),
            }),
          })
          
          if (updateResponse.ok) {
            updateSuccess = true
          }
        }
      }
      
      if (!updateSuccess) {
        const errorText = await updateResponse.text()
        console.error('Failed to update user with ime and prezime. Response:', errorText)
      }
      
      // Wait a bit for Strapi to process the update
      await new Promise(resolve => setTimeout(resolve, 500))
      
      // Fetch updated user data
      const updatedUserResponse = await fetch(`${API_URL}/api/users/me`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${registerResponse.jwt}`,
        },
      })
      
      if (updatedUserResponse.ok) {
        const updatedUserData = await updatedUserResponse.json()
        // Handle both Strapi v4 and v5 response formats
        const updatedUser = updatedUserData.data || updatedUserData
        return {
          ...registerResponse,
          user: {
            ...registerResponse.user,
            ime: updatedUser.ime || ime.trim(),
            prezime: updatedUser.prezime || prezime.trim(),
          },
        }
      }
    } catch (updateError) {
      console.error('Failed to update user with ime and prezime:', updateError)
      // Continue anyway - user is registered, just without ime/prezime
    }
  }

  return registerResponse
}

export async function checkEmailExists(email: string): Promise<boolean> {
  try {
    const response = await fetch(`${API_URL}/api/users?filters[email][$eq]=${encodeURIComponent(email)}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    })

    if (!response.ok) {
      return false // If we can't check, assume it doesn't exist
    }

    const data = await response.json()
    return data.data && data.data.length > 0
  } catch (error) {
    console.error('Error checking email:', error)
    return false
  }
}

export async function checkUsernameExists(username: string): Promise<boolean> {
  try {
    const response = await fetch(`${API_URL}/api/users?filters[username][$eq]=${encodeURIComponent(username)}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    })

    if (!response.ok) {
      return false // If we can't check, assume it doesn't exist
    }

    const data = await response.json()
    return data.data && data.data.length > 0
  } catch (error) {
    console.error('Error checking username:', error)
    return false
  }
}

/** Flatten Strapi v4/v5 user + normalize favorite_services (array or { data: [] }). */
function normalizeUsersMePayload(raw: Record<string, unknown> | null | undefined) {
  if (!raw) return raw
  const base = (raw as { attributes?: Record<string, unknown>; id?: number }).attributes
    ? {
        id: (raw as { id?: number }).id,
        documentId: (raw as { documentId?: string }).documentId,
        ...(raw as { attributes: Record<string, unknown> }).attributes,
      }
    : raw
  const o = base as Record<string, unknown>
  let fs = o.favorite_services
  if (fs && typeof fs === 'object' && fs !== null && 'data' in fs && Array.isArray((fs as { data: unknown }).data)) {
    fs = (fs as { data: unknown[] }).data
  }
  return { ...o, favorite_services: fs }
}

export async function getMe(token: string) {
  // Strapi 5: avoid populate=* on /users/me — deep populate can 400 (nested relations).
  // Shallow populate is enough for favorite_services ids.
  const urls = [
    `${API_URL}/api/users/me?populate[favorite_services]=true`,
    `${API_URL}/api/users/me?populate=*`,
    `${API_URL}/api/users/me`,
  ]

  let fallbackUserData: any = null
  for (const url of urls) {
    const r = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
    })
    if (!r.ok) continue

    const userData = await r.json()
    const rawUser = userData.data ?? userData
    const user = normalizeUsersMePayload(rawUser as Record<string, unknown>) as User
    const favorites = (user as { favorite_services?: unknown }).favorite_services
    const hasFavoritesField = Array.isArray(favorites)

    const normalizedPayload = {
      ...userData,
      data: user,
      ime: user.ime,
      prezime: user.prezime,
    }

    // Prefer payloads that include favorite_services explicitly.
    if (hasFavoritesField) return normalizedPayload
    if (!fallbackUserData) fallbackUserData = normalizedPayload
  }

  if (!fallbackUserData) {
    console.warn('/api/users/me not available, using stored user data')
    return null
  }
  return fallbackUserData
}

/** Extract numeric service ids from User.favorite_services (Strapi relation shapes). */
function favoriteServiceIdsFromUserRecord(user: Record<string, unknown> | null | undefined): number[] {
  if (!user) return []
  let fs: unknown = user.favorite_services
  if (
    fs &&
    typeof fs === 'object' &&
    fs !== null &&
    'data' in fs &&
    Array.isArray((fs as { data: unknown }).data)
  ) {
    fs = (fs as { data: unknown[] }).data
  }
  if (!Array.isArray(fs)) return []
  return fs
    .map((row: unknown) => {
      if (typeof row === 'number') return row
      if (row && typeof row === 'object' && 'id' in (row as Record<string, unknown>)) {
        return Number((row as Record<string, unknown>).id)
      }
      return NaN
    })
    .filter((n): n is number => Number.isFinite(n) && n > 0)
}

/**
 * Persist favourite marketplace services on the logged-in user.
 * Uses PUT /api/users/:id with { favorite_services } — handled by users-permissions extension
 * (favorite_services merged via userService.edit, not stripped as unknown field).
 */
export async function updateUserFavoriteServices(
  token: string,
  serviceIds: number[]
): Promise<void> {
  const me = await getMe(token)
  const meUser = (me?.data ?? me) as { id?: number } | null
  const userId = Number(meUser?.id)
  if (!Number.isFinite(userId) || userId <= 0) {
    throw new Error('Greška pri čuvanju omiljenih usluga: korisnik nije prijavljen')
  }

  const body = { favorite_services: serviceIds }
  const response = await fetch(`${API_URL}/api/users/${userId}`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(body),
  })

  if (!response.ok) {
    const t = await response.text()
    console.error('updateUserFavoriteServices', response.status, t)
    throw new Error(`Greška pri čuvanju omiljenih usluga: ${response.status}`)
  }
}

/** Source of truth: GET /api/users/me with populate[favorite_services] (same as getMe). */
export async function getUserFavoriteServiceIds(token: string): Promise<number[]> {
  const me = await getMe(token)
  const user = (me?.data ?? me) as Record<string, unknown> | null
  return favoriteServiceIdsFromUserRecord(user ?? undefined)
}

export async function forgotPassword(email: string): Promise<void> {
  const response = await fetch(`${API_URL}/api/auth/forgot-password`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      email,
    }),
  })

  if (!response.ok) {
    let errorMessage = 'Greška pri slanju emaila za reset lozinke'
    try {
      const error: AuthError = await response.json()
      errorMessage = translateError(error.error?.message || errorMessage)
      
      // Provide more helpful error messages
      if (response.status === 500) {
        errorMessage = 'Email servis nije konfigurisan. Molimo kontaktirajte administratora.'
      }
    } catch (e) {
      // If response is not JSON, use default message
      if (response.status === 500) {
        errorMessage = 'Email servis nije konfigurisan. Molimo kontaktirajte administratora.'
      }
    }
    throw new Error(errorMessage)
  }

  // Strapi returns 200 even if email doesn't exist (for security)
  return
}

export async function emailConfirmation(confirmationCode: string): Promise<LoginResponse> {
  // Strapi email confirmation endpoint expects the confirmation token as a query parameter
  const response = await fetch(`${API_URL}/api/auth/email-confirmation?confirmation=${encodeURIComponent(confirmationCode)}`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
    },
  })

  if (!response.ok) {
    const error: AuthError = await response.json()
    const errorMessage = translateError(error.error?.message || 'Greška pri potvrdi emaila')
    throw new Error(errorMessage)
  }

  return response.json()
}

export async function resetPassword(
  code: string,
  password: string,
  passwordConfirmation: string
): Promise<LoginResponse> {
  const response = await fetch(`${API_URL}/api/auth/reset-password`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      code,
      password,
      passwordConfirmation,
    }),
  })

  if (!response.ok) {
    const error: AuthError = await response.json()
    const errorMessage = translateError(error.error?.message || 'Greška pri resetovanju lozinke')
    throw new Error(errorMessage)
  }

  return response.json()
}

export async function changePassword(
  token: string,
  currentPassword: string,
  password: string,
  passwordConfirmation: string
): Promise<void> {
  const response = await fetch(`${API_URL}/api/auth/change-password`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({
      currentPassword,
      password,
      passwordConfirmation,
    }),
  })

  if (!response.ok) {
    let errorMessage = 'Greška pri promjeni lozinke'
    try {
      // Try to read as text first, then parse if possible
      const text = await response.text()
      if (text) {
        try {
          const parsed = JSON.parse(text)
          errorMessage = parsed.error?.message || parsed.message || errorMessage
        } catch (parseError) {
          // If not JSON, use the text as error message
          errorMessage = text || errorMessage
        }
      }
    } catch (e) {
      // If we can't read the response, use default message
      console.error('Error reading response:', e)
    }
    throw new Error(translateError(errorMessage))
  }

  return
}

export interface Photographer {
  id: number
  documentId?: string
  name: string
  email?: string
  phone?: string
  description?: string
  user?: number | { id: number }
  createdAt?: string
  updatedAt?: string
  publishedAt?: string
  // Support both v4 (with attributes) and v5 (flat) structures
  attributes?: {
    name: string
    email?: string
    phone?: string
    description?: string
    user?: number | { id: number }
    createdAt: string
    updatedAt: string
  }
}

export interface Guest {
  id?: number
  name: string
  phone?: string
  notes?: string
  confirmed?: boolean // Keep for backward compatibility
  status?: "Potvrđen" | "Na čekanju" | "Otkazan" | "Bez stola"
}

export interface GuestList {
  id: number
  documentId?: string
  name: string
  description?: string
  Description?: string  // Support capital D version
  guests?: Guest[]
  guest?: Guest[]  // Support singular version (component name)
  createdAt?: string
  updatedAt?: string
  publishedAt?: string
  // Support both v4 (with attributes) and v5 (flat) structures
  attributes?: {
    name: string
    description?: string
    Description?: string  // Support capital D version
    guests?: Guest[]
    guest?: Guest[]  // Support singular version (component name)
    createdAt: string
    updatedAt: string
  }
}

export async function getPhotographers(): Promise<Photographer[]> {
  const response = await fetch(`${API_URL}/api/photographers?populate=*`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
    },
  })

  if (!response.ok) {
    const errorText = await response.text()
    console.error('Photographers API error:', response.status, errorText)
    throw new Error(`Greška pri učitavanju fotografa: ${response.status}`)
  }

  const data = await response.json()
  console.log('Photographers API response:', data)
  const photographers = data.data || []
  
  // Normalize user field for both v4 and v5 structures
  // Filter out user-created photographers (only show system photographers)
  return photographers
    .filter((p: any) => {
      const user = p.attributes?.user || p.user
      // Only include photographers without a user (system photographers)
      return !user || (typeof user === 'object' && !user.id && !user.data) || user === null
    })
    .map((p: any) => {
      if (p.attributes) {
        return {
          ...p,
          attributes: {
            ...p.attributes,
            user: p.attributes.user?.data?.id || p.attributes.user?.id || p.attributes.user,
          },
        }
      }
      return {
        ...p,
        user: p.user?.data?.id || p.user?.id || p.user,
      }
    })
}

export async function getUserPhotographers(token: string, userId: number): Promise<Photographer[]> {
  const response = await fetch(
    `${API_URL}/api/photographers?populate=*&filters[user][id]=${userId}`,
    {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
    }
  )

  if (!response.ok) {
    // If filter fails, try to fetch all and filter client-side
    try {
      const allResponse = await fetch(`${API_URL}/api/photographers?populate=*`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      })

      if (!allResponse.ok) {
        return []
      }

      const allData = await allResponse.json()
      const allPhotographers = allData.data || []
      
      // Filter client-side
      return allPhotographers
        .filter((p: any) => {
          const user = p.attributes?.user || p.user
          const photographerUserId = typeof user === 'object' 
            ? (user?.data?.id || user?.id || user)
            : user
          return photographerUserId === userId
        })
        .map((p: any) => {
          if (p.attributes) {
            return {
              ...p,
              attributes: {
                ...p.attributes,
                user: p.attributes.user?.data?.id || p.attributes.user?.id || p.attributes.user,
              },
            }
          }
          return {
            ...p,
            user: p.user?.data?.id || p.user?.id || p.user,
          }
        })
    } catch (err) {
      console.error('Error fetching user photographers:', err)
      return []
    }
  }

  const data = await response.json()
  const photographers = data.data || []
  
  // Normalize user field
  return photographers.map((p: any) => {
    if (p.attributes) {
      return {
        ...p,
        attributes: {
          ...p.attributes,
          user: p.attributes.user?.data?.id || p.attributes.user?.id || p.attributes.user,
        },
      }
    }
    return {
      ...p,
      user: p.user?.data?.id || p.user?.id || p.user,
    }
  })
}

export async function createPhotographer(
  token: string,
  name: string,
  phone: string | undefined,
  userId: number
): Promise<Photographer> {
  const payload: any = {
    data: {
      name: name.trim(),
      user: userId, // Associate photographer with user
    },
  }

  if (phone && phone.trim()) {
    payload.data.phone = phone.trim()
  }

  console.log('Creating photographer with payload:', JSON.stringify(payload, null, 2))

  const response = await fetch(`${API_URL}/api/photographers`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(payload),
  })

  if (!response.ok) {
    const errorText = await response.text()
    console.error('Create photographer API error:', response.status, errorText)
    throw new Error(`Greška pri kreiranju fotografa: ${response.status}`)
  }

  const data = await response.json()
  console.log('Create photographer API response:', data)
  const created = data.data || data
  
  // Normalize the response
  if (created.attributes) {
    return {
      ...created,
      attributes: {
        ...created.attributes,
        user: created.attributes.user?.data?.id || created.attributes.user?.id || created.attributes.user || userId,
      },
    }
  }
  
  return {
    ...created,
    user: created.user?.data?.id || created.user?.id || created.user || userId,
  }
}

export async function updatePhotographer(
  token: string,
  id: number,
  documentId: string | undefined,
  name: string,
  phone?: string
): Promise<Photographer> {
  const payload: any = {
    data: {
      name: name.trim(),
    },
  }

  if (phone !== undefined) {
    payload.data.phone = phone ? phone.trim() : null
  }

  console.log('Updating photographer with payload:', JSON.stringify(payload, null, 2))

  const identifier = documentId || id
  const response = await fetch(`${API_URL}/api/photographers/${identifier}`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(payload),
  })

  if (!response.ok) {
    const errorText = await response.text()
    console.error('Update photographer API error:', response.status, errorText)
    throw new Error(`Greška pri ažuriranju fotografa: ${response.status}`)
  }

  const data = await response.json()
  console.log('Update photographer API response:', data)
  return data.data || data
}

export async function deletePhotographer(
  token: string,
  id: number,
  documentId?: string
): Promise<void> {
  const identifier = documentId || id
  
  const response = await fetch(`${API_URL}/api/photographers/${identifier}`, {
    method: 'DELETE',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
  })

  if (!response.ok) {
    const errorText = await response.text()
    console.error('Delete photographer API error:', response.status, errorText)
    throw new Error(`Greška pri brisanju fotografa: ${response.status}`)
  }
}

export async function updateUserProfile(
  token: string,
  userId: number,
  selectedPhotographers: number[]
) {
  console.log('Updating user profile:', { userId, selectedPhotographers })
  
  // Build the update payload - Strapi users API typically doesn't use data wrapper
  // Try with object format first (Strapi v5 format for relations)
  const updatePayload: any = {
    photographers: selectedPhotographers.map(id => ({ id })),
  }

  console.log('Update payload:', JSON.stringify(updatePayload, null, 2))
  
  // Update user using their ID - Strapi uses /api/users/:id
  // The schema shows photographers as oneToMany, but we need manyToMany
  // Try with simple ID array first (most compatible format)
  const simplePayload: any = {
    photographers: selectedPhotographers,
  }
  
  console.log('Simple payload:', JSON.stringify(simplePayload, null, 2))
  
  let updateResponse = await fetch(`${API_URL}/api/users/${userId}`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(simplePayload),
  })

  console.log('Update response status:', updateResponse.status)

  // If that doesn't work, try with object format (Strapi v5 format for relations)
  if (!updateResponse.ok) {
    const errorText = await updateResponse.text()
    console.log('Simple payload attempt failed, error:', errorText)
    
    updateResponse = await fetch(`${API_URL}/api/users/${userId}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(updatePayload),
    })
    
    console.log('Object format response status:', updateResponse.status)
  }

  if (!updateResponse.ok) {
    const errorText = await updateResponse.text()
    let error
    try {
      error = JSON.parse(errorText)
    } catch {
      error = { error: { message: errorText || 'Unknown error' } }
    }
    console.error('Update user profile error:', error, 'Status:', updateResponse.status)
    console.error('Full error response:', errorText)
    
    // If it's a 400 error, it might mean the photographers field doesn't exist
    if (updateResponse.status === 400) {
      throw new Error('Polje "photographers" ne postoji u Strapi. Molimo restartujte Strapi server da bi se ekstenzija učitala.')
    }
    
    // If it's a 500 error, provide more helpful message
    if (updateResponse.status === 500) {
      const errorMessage = translateError(error.error?.message || errorText || 'Internal Server Error')
      throw new Error(`Greška na serveru: ${errorMessage}. Proverite da li postoji relacija "photographers" u User modelu u Strapi i da li imate dozvole za ažuriranje korisnika.`)
    }
    
    const errorMsg = error.error?.message || `Greška pri čuvanju izbora (${updateResponse.status})`
    throw new Error(translateError(errorMsg))
  }

  const result = await updateResponse.json()
  console.log('User profile updated successfully:', result)
  
  // After update, fetch the user again to get the updated data
  // Try with populate=* first, then without populate if that fails
  try {
    let updatedUser = await fetch(`${API_URL}/api/users/${userId}?populate=*`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
    })
    
    if (!updatedUser.ok) {
      // Try without populate
      updatedUser = await fetch(`${API_URL}/api/users/${userId}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      })
    }
    
    if (updatedUser.ok) {
      const userData = await updatedUser.json()
      console.log('Fetched updated user after save:', userData)
      const userObj = userData.data || userData
      
      if (userObj.photographers !== undefined) {
        console.log('Photographers field exists in response:', userObj.photographers)
      } else {
        console.warn('Photographers field not found in user response. Relation may not exist.')
      }

      return userObj
    }
  } catch (fetchError) {
    console.warn('Could not fetch updated user, returning update result:', fetchError)
  }
  
  // Strapi might return data in { data: {...} } format or directly
  // Return the data part if it exists, otherwise return the whole result
  return result.data || result
}

export async function getGuestLists(
  token: string,
  userId?: number,
  plannerEventId?: number
): Promise<GuestList[]> {
  let url = `${API_URL}/api/guest-lists?populate=*`

  if (userId) {
    url = `${API_URL}/api/guest-lists?populate=*&filters[user][id][$eq]=${userId}`
    if (plannerEventId != null) {
      url += `&filters[planner_event][id][$eq]=${plannerEventId}`
    }
  }
  
  const response = await fetch(url, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
  })

  if (!response.ok) {
    // If filter fails, try without filter (get all and filter client-side)
    if (response.status === 400 && userId) {
      console.warn('Filter failed, trying without filter and filtering client-side')
      const allResponse = await fetch(`${API_URL}/api/guest-lists?populate=*`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      })
      
      if (allResponse.ok) {
        const allData = await allResponse.json()
        const allLists = allData.data || []
        // Filter client-side by user
        const filteredLists = allLists.filter((list: any) => {
          const listUser = list.attributes?.user?.data || list.attributes?.user || list.user
          let userOk = false
          if (typeof listUser === 'object' && listUser !== null) {
            userOk = (listUser.id || listUser.documentId) === userId
          } else {
            userOk = listUser === userId
          }
          if (!userOk) return false
          if (plannerEventId == null) return true
          const pe =
            list.attributes?.planner_event?.data?.id ??
            list.attributes?.planner_event?.id ??
            list.planner_event?.id ??
            list.planner_event
          const pid = typeof pe === 'object' && pe !== null ? pe.id : pe
          return pid === plannerEventId
        })
        return filteredLists
      }
    }
    
    const errorText = await response.text()
    console.error('Guest lists API error:', response.status, errorText)
    throw new Error(`Greška pri učitavanju spiskova gostiju: ${response.status}`)
  }

  const data = await response.json()
  console.log('Guest lists API response:', data)
  const rows = data.data || []
  if (plannerEventId != null && userId) {
    return rows.filter((list: any) => {
      const pe =
        list.attributes?.planner_event?.data?.id ??
        list.attributes?.planner_event?.id ??
        list.planner_event?.id ??
        list.planner_event
      const pid = typeof pe === 'object' && pe !== null ? pe.id : pe
      return pid === plannerEventId
    })
  }
  return rows
}

export async function createGuestList(
  token: string,
  name: string,
  description?: string,
  guests: Guest[] = [],
  userId?: number,
  plannerEventId?: number
): Promise<GuestList> {
  // Clean guests data - only include fields that have values
  // Filter out guests without names and clean the data
  const cleanedGuests = guests
    .filter((guest) => guest.name && guest.name.trim()) // Only include guests with names
    .map((guest) => {
      const cleaned: any = {
        name: guest.name.trim(),  // Name is required
      }
      
      // Only add optional fields if they have values
      if (guest.phone && guest.phone.trim()) {
        cleaned.phone = guest.phone.trim()
      }
      if (guest.notes && guest.notes.trim()) {
        cleaned.notes = guest.notes.trim()
      }
      // Include status field if it exists
      if (guest.status) {
        cleaned.status = guest.status
      }
      // Include confirmed field if it exists (for backward compatibility)
      if (guest.confirmed !== undefined) {
        cleaned.confirmed = guest.confirmed
      }
      
      return cleaned
    })
  
  const payload: any = {
    data: {
      name: name.trim(),
    },
  }
  
  // Only add guest component if there are guests
  if (cleanedGuests.length > 0) {
    payload.data.guest = cleanedGuests  // Use "guest" (singular) as that's the component name in Strapi
    console.log('Adding', cleanedGuests.length, 'guests to payload:', cleanedGuests)
  } else {
    console.log('No guests to add (cleanedGuests is empty or filtered out)')
  }
  
  // Only add description if it's provided (field is "Description" with capital D in Strapi)
  if (description && description.trim()) {
    payload.data.Description = description.trim()
  }
  
  // Add user relation if userId is provided
  // Try different formats for Strapi v5
  if (userId) {
    payload.data.user = { id: userId }
  }
  if (plannerEventId != null) {
    payload.data.planner_event = plannerEventId
  }

  console.log('Creating guest list with payload:', JSON.stringify(payload, null, 2))
  
  let response = await fetch(`${API_URL}/api/guest-lists`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(payload),
  })

  // If that fails and we have userId, try without user relation
  if (!response.ok && userId) {
    console.log('First attempt with user relation failed, trying without user relation')
    const retryPayload = { ...payload }
    delete retryPayload.data.user
    
    response = await fetch(`${API_URL}/api/guest-lists`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(retryPayload),
    })
    
    // If creation succeeds without user, log a warning
    if (response.ok) {
      console.warn('Guest list created without user relation. The user relation may need to be set up differently in Strapi.')
      console.warn('You may need to manually assign the list to a user in Strapi admin panel.')
    }
  }

  if (!response.ok) {
    const errorText = await response.text()
    console.error('Create guest list API error:', response.status, errorText)
    
    // Try to parse error for more details
    try {
      const errorJson = JSON.parse(errorText)
      if (errorJson.error?.message) {
        throw new Error(`Greška pri kreiranju spiska gostiju: ${translateError(errorJson.error.message)}`)
      }
    } catch (e) {
      // If parsing fails, use generic error
    }
    
    throw new Error(`Greška pri kreiranju spiska gostiju: ${response.status}`)
  }

  const data = await response.json()
  console.log('Create guest list API response:', data)
  const createdList = data.data || data
  
  // If the response doesn't include guests, fetch the full list with populate
  if (!createdList.guest && !createdList.guests && !createdList.attributes?.guest) {
    console.log('Response does not include guests, fetching full list with populate')
    try {
      const listId = createdList.documentId || createdList.id
      const fullResponse = await fetch(`${API_URL}/api/guest-lists/${listId}?populate=*`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      })
      
      if (fullResponse.ok) {
        const fullData = await fullResponse.json()
        console.log('Fetched full list with guests:', fullData)
        return fullData.data || fullData
      }
    } catch (fetchError) {
      console.warn('Could not fetch full list with guests:', fetchError)
    }
  }
  
  return createdList
}

export async function updateGuestList(
  token: string,
  id: number,
  name: string,
  description?: string,
  guests: Guest[] = [],
  documentId?: string,
  plannerEventId?: number
): Promise<GuestList> {
  // Clean guests data - only include fields that have values
  // Filter out guests without names and clean the data
  const cleanedGuests = guests
    .filter((guest) => guest.name && guest.name.trim()) // Only include guests with names
    .map((guest) => {
      const cleaned: any = {
        name: guest.name.trim(),  // Name is required
      }
      
      // Only add optional fields if they have values
      if (guest.phone && guest.phone.trim()) {
        cleaned.phone = guest.phone.trim()
      }
      if (guest.notes && guest.notes.trim()) {
        cleaned.notes = guest.notes.trim()
      }
      // Include status field if it exists
      if (guest.status) {
        cleaned.status = guest.status
      }
      // Include confirmed field if it exists (for backward compatibility)
      if (guest.confirmed !== undefined) {
        cleaned.confirmed = guest.confirmed
      }
      
      return cleaned
    })
  
  const payload: any = {
    data: {
      name: name.trim(),
    },
  }
  
  // Always include guest component (even if empty array) to ensure it's updated correctly
  // Use "guest" (singular) as that's the component name in Strapi
  payload.data.guest = cleanedGuests
  console.log('Updating guest list with', cleanedGuests.length, 'guests:', cleanedGuests)
  
  // Only add description if it's provided (field is "Description" with capital D in Strapi)
  if (description !== undefined && description.trim()) {
    payload.data.Description = description.trim()
  }

  if (plannerEventId != null) {
    payload.data.planner_event = plannerEventId
  }
  
  console.log('Update guest list payload:', JSON.stringify(payload, null, 2))
  
  // Try with documentId first (Strapi v5), then fall back to id
  const identifier = documentId || id
  let response = await fetch(`${API_URL}/api/guest-lists/${identifier}`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(payload),
  })

  // If that fails with 404, it's likely a permissions issue
  if (!response.ok && response.status === 404) {
    console.warn('Update with identifier failed, this is likely a permissions issue')
    const errorText = await response.text()
    console.error('Update guest list API error:', response.status, errorText)
    
    throw new Error(`Greška pri ažuriranju spiska gostiju: Nemate dozvole za ažuriranje. Molimo proverite dozvole u Strapi admin panelu za "Guest List" content type.`)
  }

  if (!response.ok) {
    const errorText = await response.text()
    console.error('Update guest list API error:', response.status, errorText)
    throw new Error(`Greška pri ažuriranju spiska gostiju: ${response.status}`)
  }

  const data = await response.json()
  console.log('Update guest list API response:', data)
  return data.data || data
}

export async function deleteGuestList(token: string, id: number, documentId?: string): Promise<void> {
  // Try with documentId first (Strapi v5), then fall back to id
  const identifier = documentId || id
  
  const response = await fetch(`${API_URL}/api/guest-lists/${identifier}`, {
    method: 'DELETE',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
  })

  if (!response.ok) {
    const errorText = await response.text()
    console.error('Delete guest list API error:', response.status, errorText)
    
    if (response.status === 404) {
      throw new Error(`Greška pri brisanju spiska gostiju: Nemate dozvole za brisanje. Molimo proverite dozvole u Strapi admin panelu za "Guest List" content type.`)
    }
    
    throw new Error(`Greška pri brisanju spiska gostiju: ${response.status}`)
  }
}

// Budget and Expense interfaces
export interface Expense {
  id?: number
  name: string
  amount: number
  paid: boolean
}

export interface Budget {
  id?: number
  documentId?: string
  budget: number
  expense?: Expense[]
  user?: number | { id: number }
  createdAt?: string
  updatedAt?: string
  publishedAt?: string
}

// Get user's budget (optionally scoped to a planner event)
export async function getBudget(
  token: string,
  userId: number,
  plannerEventId?: number
): Promise<Budget | null> {
  try {
    let budgetUrl = `${API_URL}/api/budgets?populate=*&filters[user][id][$eq]=${userId}`
    if (plannerEventId != null) {
      budgetUrl += `&filters[planner_event][id][$eq]=${plannerEventId}`
    }
    const response = await fetch(budgetUrl, {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      }
    )

    if (!response.ok) {
      if (response.status === 404 || response.status === 403) {
        return null
      }
      const errorText = await response.text()
      console.error('Budget API error:', response.status, errorText)
      throw new Error(`Greška pri učitavanju budžeta: ${response.status}`)
    }

    const data = await response.json()
    
    // Handle both v4 and v5 response structures
    const budgets = data.data || (Array.isArray(data) ? data : [])
    
    if (budgets.length === 0) {
      return null
    }

    let budget: (typeof budgets)[0]
    if (plannerEventId != null) {
      const match = budgets.find((b: any) => {
        const pe =
          b.attributes?.planner_event?.data?.id ??
          b.attributes?.planner_event?.id ??
          b.planner_event?.id ??
          b.planner_event
        const pid = typeof pe === 'object' && pe !== null ? pe.id : pe
        return pid === plannerEventId
      })
      if (!match) {
        return null
      }
      budget = match
    } else {
      budget = budgets[0]
    }
    
    // Handle both v4 (with attributes) and v5 (flat) structures
    if (budget.attributes) {
      return {
        id: budget.id,
        documentId: budget.documentId,
        budget: budget.attributes.budget || 0,
        expense: budget.attributes.expense || [],
        user: budget.attributes.user?.data?.id || budget.attributes.user,
        createdAt: budget.attributes.createdAt,
        updatedAt: budget.attributes.updatedAt,
        publishedAt: budget.attributes.publishedAt,
      }
    }

    return {
      id: budget.id,
      documentId: budget.documentId,
      budget: budget.budget || 0,
      expense: budget.expense || [],
      user: budget.user?.id || budget.user,
      createdAt: budget.createdAt,
      updatedAt: budget.updatedAt,
      publishedAt: budget.publishedAt,
    }
  } catch (err) {
    console.error('Error fetching budget:', err)
    // If filter fails, try to fetch all and filter client-side
    try {
      const response = await fetch(`${API_URL}/api/budgets?populate=*`, {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      })

      if (!response.ok) {
        return null
      }

      const data = await response.json()
      const budgets = data.data || (Array.isArray(data) ? data : [])
      const userBudget = budgets.find((b: any) => {
        const user = b.attributes?.user?.data?.id || b.attributes?.user || b.user?.id || b.user
        if (user !== userId) return false
        if (plannerEventId == null) return true
        const pe =
          b.attributes?.planner_event?.data?.id ??
          b.attributes?.planner_event?.id ??
          b.planner_event?.id ??
          b.planner_event
        const pid = typeof pe === 'object' && pe !== null ? pe.id : pe
        return pid === plannerEventId
      })

      if (!userBudget) {
        return null
      }

      const budget = userBudget
      if (budget.attributes) {
        return {
          id: budget.id,
          documentId: budget.documentId,
          budget: budget.attributes.budget || 0,
          expense: budget.attributes.expense || [],
          user: budget.attributes.user?.data?.id || budget.attributes.user,
          createdAt: budget.attributes.createdAt,
          updatedAt: budget.attributes.updatedAt,
          publishedAt: budget.attributes.publishedAt,
        }
      }

      return {
        id: budget.id,
        documentId: budget.documentId,
        budget: budget.budget || 0,
        expense: budget.expense || [],
        user: budget.user?.id || budget.user,
        createdAt: budget.createdAt,
        updatedAt: budget.updatedAt,
        publishedAt: budget.publishedAt,
      }
    } catch (fallbackErr) {
      console.error('Fallback budget fetch failed:', fallbackErr)
      return null
    }
  }
}

// Create or update budget
export async function saveBudget(
  token: string,
  budget: number,
  expenses: Expense[],
  userId: number,
  existingBudgetId?: number,
  documentId?: string,
  plannerEventId?: number
): Promise<Budget> {
  // Clean expenses - only include non-empty required fields
  // Filter out expenses without names and ensure amounts are valid
  const cleanedExpenses = expenses
    .filter((exp) => exp.name && exp.name.trim()) // Only include expenses with names
    .map((exp) => {
      const amount = parseFloat(exp.amount.toString()) || 0
      return {
        name: exp.name.trim(),
        amount: amount, // Keep as number, Strapi will handle decimal conversion
        paid: exp.paid || false,
      }
    })

  if (existingBudgetId || documentId) {
    // Update existing budget
    // Use documentId (Strapi v5 format)
    const identifier = documentId || existingBudgetId
    const budgetValue = parseFloat(budget.toString()) || 0

    /** Keep budget row tied to the active planner event (legacy rows may lack relation). */
    const withPlannerEvent = (data: Record<string, unknown>) => {
      if (plannerEventId == null) return data
      return { ...data, planner_event: plannerEventId }
    }
    
    // Ensure budget is a valid number
    if (isNaN(budgetValue) || budgetValue < 0) {
      throw new Error('Budžet mora biti validan pozitivan broj')
    }
    
    // Filter out invalid expenses
    const validExpenses = cleanedExpenses.filter(
      (exp) => exp.name && exp.name.trim() !== '' && !isNaN(exp.amount) && exp.amount >= 0
    )
    
    // First, try to fetch the current budget to verify it exists and get its structure
    let currentBudget = null
    try {
      const fetchResponse = await fetch(`${API_URL}/api/budgets/${identifier}?populate=*`, {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      })
      
      if (fetchResponse.ok) {
        const fetchData = await fetchResponse.json()
        currentBudget = fetchData.data || fetchData
        console.log('Current budget fetched:', currentBudget)
      } else {
        console.warn('Could not fetch current budget, proceeding with update anyway')
      }
    } catch (fetchErr) {
      console.warn('Error fetching current budget:', fetchErr)
    }
    
    // Try different payload formats
    // Format 1: Budget as number, expenses as array
    let payload: any = {
      data: withPlannerEvent({
        budget: budgetValue,
        expense: validExpenses,
      }),
    }

    console.log('Updating budget with payload:', JSON.stringify(payload, null, 2))
    console.log('Using identifier:', identifier, '(documentId:', documentId, ', existingBudgetId:', existingBudgetId, ')')

    let response = await fetch(`${API_URL}/api/budgets/${identifier}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(payload),
    })
    
    // Format 2: Budget as string (for decimal fields)
    if (!response.ok && response.status === 500) {
      console.log('Format 1 failed, trying budget as string')
      payload = {
        data: withPlannerEvent({
          budget: budgetValue.toString(),
          expense: validExpenses,
        }),
      }
      
      response = await fetch(`${API_URL}/api/budgets/${identifier}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      })
    }
    
    // Format 3: Try PATCH instead of PUT
    if (!response.ok && response.status === 500) {
      console.log('PUT failed, trying PATCH')
      payload = {
        data: withPlannerEvent({
          budget: budgetValue,
          expense: validExpenses,
        }),
      }
      
      response = await fetch(`${API_URL}/api/budgets/${identifier}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      })
    }
    
    // Format 4: Update separately (budget first, then expenses)
    if (!response.ok && response.status === 500) {
      console.log('Combined update failed, trying separate updates')
      
      // Update budget only
      const budgetPayload = {
        data: withPlannerEvent({
          budget: budgetValue,
        }),
      }
      
      response = await fetch(`${API_URL}/api/budgets/${identifier}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(budgetPayload),
      })
      
      // If budget update succeeds, update expenses
      if (response.ok && validExpenses.length > 0) {
        console.log('Budget updated, now updating expenses')
        const expensesPayload = {
          data: {
            expense: validExpenses,
          },
        }
        
        response = await fetch(`${API_URL}/api/budgets/${identifier}`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(expensesPayload),
        })
      }
    }

    if (!response.ok) {
      const errorText = await response.text()
      console.error('Update budget API error:', response.status)
      console.error('Error response body:', errorText)
      console.error('Request payload was:', JSON.stringify(payload, null, 2))
      
      let errorMessage = `Greška pri ažuriranju budžeta: ${response.status}`
      
      // Try to parse error for more details
      try {
        const errorJson = JSON.parse(errorText)
        console.error('Parsed error JSON:', errorJson)
        
        if (errorJson.error?.message) {
          errorMessage = `Greška pri ažuriranju budžeta: ${translateError(errorJson.error.message)}`
        } else if (errorJson.error?.details) {
          const detailsStr = typeof errorJson.error.details === 'string' 
            ? errorJson.error.details 
            : JSON.stringify(errorJson.error.details)
          errorMessage = `Greška pri ažuriranju budžeta: ${translateError(detailsStr)}`
        } else if (errorJson.message) {
          errorMessage = `Greška pri ažuriranju budžeta: ${translateError(errorJson.message)}`
        }
      } catch (parseErr) {
        console.error('Failed to parse error response:', parseErr)
        // Use default error message
      }
      
      throw new Error(errorMessage)
    }

    const data = await response.json()
    console.log('Update budget API response:', data)
    return data.data || data
  } else {
    // Create new budget
    const payload: any = {
      data: {
        budget: parseFloat(budget.toString()) || 0,
        expense: cleanedExpenses,
      },
    }
    if (plannerEventId != null) {
      payload.data.planner_event = plannerEventId
    }

    console.log('Creating budget with payload:', JSON.stringify(payload, null, 2))

    let response = await fetch(`${API_URL}/api/budgets`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(payload),
    })

    if (!response.ok) {
      payload.data.user = userId
      if (plannerEventId != null) {
        payload.data.planner_event = plannerEventId
      }
      response = await fetch(`${API_URL}/api/budgets`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      })
    }

    if (!response.ok) {
      const errorText = await response.text()
      console.error('Create budget API error:', response.status, errorText)
      throw new Error(`Greška pri kreiranju budžeta: ${response.status}`)
    }

    const data = await response.json()
    const createdBudget = data.data || data

    // Try to add user + planner_event relation separately if it wasn't included
    if (createdBudget.id || createdBudget.documentId) {
      const identifier = createdBudget.documentId || createdBudget.id
      try {
        const updatePayload: any = {
          data: {
            user: userId,
          },
        }
        if (plannerEventId != null) {
          updatePayload.data.planner_event = plannerEventId
        }

        await fetch(`${API_URL}/api/budgets/${identifier}`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(updatePayload),
        })
      } catch (err) {
        console.warn('Could not add user relation, but budget was created')
      }
    }

    return createdBudget
  }
}

// Todo interfaces
export interface Todo {
  id: number
  documentId?: string
  title: string
  completed: boolean
  createdAt?: string
  updatedAt?: string
  user?: number
  attributes?: {
    title: string
    completed: boolean
    createdAt?: string
    updatedAt?: string
    user?: any
  }
}

export async function getTodos(
  token: string,
  userId?: number,
  plannerEventId?: number
): Promise<Todo[]> {
  let url = `${API_URL}/api/todos?populate=*`

  if (userId) {
    url = `${API_URL}/api/todos?populate=*&filters[user][id][$eq]=${userId}`
    if (plannerEventId != null) {
      url += `&filters[planner_event][id][$eq]=${plannerEventId}`
    }
  }
  
  const response = await fetch(url, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
  })

  if (!response.ok) {
    if (response.status === 400 && userId) {
      console.warn('Filter failed, trying without filter and filtering client-side')
      const allResponse = await fetch(`${API_URL}/api/todos?populate=*`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      })
      
      if (allResponse.ok) {
        const allData = await allResponse.json()
        const allTodos = allData.data || []
        const filteredTodos = allTodos.filter((todo: any) => {
          const todoUser = todo.attributes?.user?.data || todo.attributes?.user || todo.user
          let userOk = false
          if (typeof todoUser === 'object' && todoUser !== null) {
            userOk = (todoUser.id || todoUser.documentId) === userId
          } else {
            userOk = todoUser === userId
          }
          if (!userOk) return false
          if (plannerEventId == null) return true
          const pe =
            todo.attributes?.planner_event?.data?.id ??
            todo.attributes?.planner_event?.id ??
            todo.planner_event?.id ??
            todo.planner_event
          const pid = typeof pe === 'object' && pe !== null ? pe.id : pe
          return pid === plannerEventId
        })
        return filteredTodos
      }
    }
    
    const errorText = await response.text()
    console.error('Todos API error:', response.status, errorText)
    throw new Error(`Greška pri učitavanju zadataka: ${response.status}`)
  }

  const data = await response.json()
  const rows = data.data || []
  if (plannerEventId != null && userId) {
    return rows.filter((todo: any) => {
      const pe =
        todo.attributes?.planner_event?.data?.id ??
        todo.attributes?.planner_event?.id ??
        todo.planner_event?.id ??
        todo.planner_event
      const pid = typeof pe === 'object' && pe !== null ? pe.id : pe
      return pid === plannerEventId
    })
  }
  return rows
}

export async function createTodo(
  token: string,
  title: string,
  userId?: number,
  plannerEventId?: number,
  /** Strapi 5 many-to-one: prefer planner-event `documentId` (string); numeric id alone may be rejected. */
  plannerEventDocumentId?: string | null
): Promise<Todo> {
  const payload: any = {
    data: {
      title: title.trim(),
      completed: false,
    },
  }
  
  if (userId) {
    payload.data.user = { id: userId }
  }
  if (plannerEventDocumentId) {
    payload.data.planner_event = plannerEventDocumentId
  } else if (plannerEventId != null) {
    payload.data.planner_event = plannerEventId
  }
  
  const response = await fetch(`${API_URL}/api/todos`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(payload),
  })

  if (!response.ok) {
    const errorText = await response.text()
    console.error('Create todo API error:', response.status, errorText)
    throw new Error(`Greška pri kreiranju zadatka: ${response.status}`)
  }

  const data = await response.json()
  return data.data || data
}

export async function updateTodo(
  token: string,
  id: number,
  title: string,
  completed: boolean,
  documentId?: string
): Promise<Todo> {
  const payload: any = {
    data: {
      title: title.trim(),
      completed,
    },
  }
  
  const identifier = documentId || id
  const response = await fetch(`${API_URL}/api/todos/${identifier}`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(payload),
  })

  if (!response.ok) {
    const errorText = await response.text()
    console.error('Update todo API error:', response.status, errorText)
    throw new Error(`Greška pri ažuriranju zadatka: ${response.status}`)
  }

  const data = await response.json()
  return data.data || data
}

export async function toggleTodo(
  token: string,
  id: number,
  completed: boolean,
  documentId?: string
): Promise<Todo> {
  const identifier = documentId || id
  
  // First get the current todo to preserve the title
  const getResponse = await fetch(`${API_URL}/api/todos/${identifier}?populate=*`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
  })
  
  if (!getResponse.ok) {
    throw new Error(`Greška pri učitavanju zadatka: ${getResponse.status}`)
  }
  
  const getData = await getResponse.json()
  const currentTodo = getData.data || getData
  const currentTitle = currentTodo.attributes?.title || currentTodo.title || ''
  
  // Update with the same title but toggled completed status
  return updateTodo(token, id, currentTitle, completed, documentId)
}

export async function deleteTodo(token: string, id: number, documentId?: string): Promise<void> {
  const identifier = documentId || id
  
  const response = await fetch(`${API_URL}/api/todos/${identifier}`, {
    method: 'DELETE',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
  })

  if (!response.ok) {
    const errorText = await response.text()
    console.error('Delete todo API error:', response.status, errorText)
    throw new Error(`Greška pri brisanju zadatka: ${response.status}`)
  }
}

export async function deleteBudgetById(
  token: string,
  id: number,
  documentId?: string
): Promise<void> {
  const identifier = documentId || id
  const response = await fetch(`${API_URL}/api/budgets/${identifier}`, {
    method: 'DELETE',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
  })

  if (!response.ok && response.status !== 404) {
    const errorText = await response.text()
    console.error('Delete budget API error:', response.status, errorText)
    throw new Error(`Greška pri brisanju budžeta: ${response.status}`)
  }
}

/**
 * Deletes guest lists, todos, and budget row for a planner event.
 * Call before deleting the planner-event row so data is not left orphaned
 * (and later mis-attached by client migration).
 */
export async function deletePlannerEventLinkedData(
  token: string,
  userId: number,
  plannerEventId: number
): Promise<void> {
  const lists = await getGuestLists(token, userId, plannerEventId)
  for (const list of lists) {
    await deleteGuestList(token, list.id, list.documentId)
  }
  const todos = await getTodos(token, userId, plannerEventId)
  for (const todo of todos) {
    await deleteTodo(token, todo.id, todo.documentId)
  }
  const budget = await getBudget(token, userId, plannerEventId)
  if (budget?.id != null) {
    await deleteBudgetById(token, budget.id, budget.documentId)
  }
}

